import React, { useState, useEffect, useCallback } from 'react';
import { loadOrCreateIdentity } from '../lib/identity';
import { getProfile } from '../lib/metadata';
import { getCommentsForVideo, saveIncomingComment, deleteComment, addCommentListener } from '../lib/comments';
import { publishEvent } from '../lib/sync';
import { pubkeyShortHash } from '../lib/sanitize';
import { COLORS, SPACING } from '../styles/theme';

export function Comments({ videoHash }: { videoHash: string }) {
  const [comments, setComments] = useState<any[]>([]);
  const [newComment, setNewComment] = useState('');
  const [identity, setIdentity] = useState<any>(null);
  const [profile, setProfile] = useState<any>(null);

  const loadComments = useCallback(() => {
    setComments(getCommentsForVideo(videoHash));
  }, [videoHash]);

  useEffect(() => {
    setIdentity(loadOrCreateIdentity());
    setProfile(getProfile());
    loadComments();
    return addCommentListener(loadComments);
  }, [videoHash, loadComments]);

  const handleAddComment = async () => {
    if (!newComment.trim() || !identity) return;
    const text = newComment.trim();
    const username = profile?.username || pubkeyShortHash(identity.publicKey);
    setNewComment('');
    const event = await publishEvent('comment', {
      videoHash,
      userId: identity.userId,
      username,
      text,
    });
    saveIncomingComment({
      id: event.id,
      videoHash,
      userId: identity.userId,
      username,
      text,
      createdAt: event.timestamp,
    });
  };

  const handleDeleteComment = async (commentId: string, userId: string) => {
    if (identity?.userId !== userId) return;
    deleteComment(commentId, userId);
    await publishEvent('delete_comment', { commentId, userId });
  };

  return (
    <div style={{
      border: `2px outset ${COLORS.highlight}`,
      backgroundColor: COLORS.panel,
      overflow: 'hidden',
      marginTop: SPACING.lg
    }}>
      <div style={{
        backgroundColor: COLORS.accent,
        color: '#fff',
        padding: `${SPACING.xs} ${SPACING.sm}`,
        fontSize: '9px',
        fontWeight: 700,
        borderBottom: `2px solid ${COLORS.highlight}`,
        userSelect: 'none'
      }}>
        Comments
      </div>

      <div style={{
        backgroundColor: COLORS.bg,
        maxHeight: '300px',
        overflowY: 'auto',
        borderBottom: `1px solid ${COLORS.border}`
      }}>
        {comments.length === 0 ? (
          <p style={{ padding: SPACING.lg, margin: 0, color: COLORS.textMuted, fontSize: '9px' }}>
            No comments
          </p>
        ) : (
          comments.map((comment, idx) => (
            <div
              key={comment.id}
              style={{
                padding: SPACING.lg,
                borderBottom: idx < comments.length - 1 ? `1px solid ${COLORS.border}` : 'none',
                backgroundColor: idx % 2 === 0 ? COLORS.bg : 'rgba(139, 30, 45, 0.05)'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: SPACING.sm }}>
                <p style={{ margin: 0, fontSize: '9px', fontWeight: 700, color: COLORS.accent }}>
                  @{comment.username}
                </p>
                <span style={{ fontSize: '8px', color: COLORS.textMuted }}>
                  {new Date(comment.createdAt).toLocaleTimeString()}
                </span>
              </div>
              <p style={{ margin: `0 0 ${SPACING.sm} 0`, fontSize: '10px', color: COLORS.text, lineHeight: 1.4 }}>
                {comment.text}
              </p>
              {identity?.userId === comment.userId && (
                <button
                  onClick={() => handleDeleteComment(comment.id, comment.userId)}
                  style={{
                    padding: `${SPACING.xs} ${SPACING.sm}`,
                    backgroundColor: 'transparent',
                    color: COLORS.accent,
                    border: `1px solid ${COLORS.accent}`,
                    cursor: 'pointer',
                    fontSize: '8px',
                    fontWeight: 700,
                    fontFamily: 'monospace',
                    textDecoration: 'underline'
                  }}
                >
                  Delete
                </button>
              )}
            </div>
          ))
        )}
      </div>

      <div style={{
        padding: SPACING.lg,
        display: 'flex',
        gap: SPACING.sm,
        borderTop: `1px solid ${COLORS.border}`,
        backgroundColor: COLORS.panel
      }}>
        <input
          type="text"
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleAddComment(); }}
          placeholder="Add comment..."
          maxLength={500}
          style={{
            flex: 1,
            padding: SPACING.md,
            backgroundColor: '#fff',
            color: '#000',
            border: `2px inset ${COLORS.highlight}`,
            fontFamily: 'monospace',
            fontSize: '10px'
          }}
        />
        <button
          onClick={handleAddComment}
          disabled={!newComment.trim()}
          style={{
            padding: `${SPACING.md} ${SPACING.lg}`,
            backgroundColor: newComment.trim() ? COLORS.accent : COLORS.panel,
            color: newComment.trim() ? '#fff' : COLORS.textMuted,
            border: `2px outset ${newComment.trim() ? COLORS.accent : COLORS.border}`,
            cursor: newComment.trim() ? 'pointer' : 'default',
            fontFamily: 'monospace',
            fontSize: '9px',
            fontWeight: 700,
            textDecoration: 'underline',
            boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.5)'
          }}
          onMouseDown={(e) => {
            if (newComment.trim()) {
              e.currentTarget.style.borderStyle = 'inset';
              e.currentTarget.style.boxShadow = 'inset 1px 1px 2px rgba(0,0,0,0.8)';
            }
          }}
          onMouseUp={(e) => {
            e.currentTarget.style.borderStyle = 'outset';
            e.currentTarget.style.boxShadow = 'inset 0 0 0 1px rgba(0,0,0,0.5)';
          }}
        >
          Post
        </button>
      </div>
    </div>
  );
}
